// 화면 표기 포맷. 컴포넌트가 아니라 순수 함수라 파일을 나눈다.

/** 값이 없는 자리. 0 도 빈 문자열도 아니다. */
export const DASH = '—'

// 지금과 견주는 함수(`ago`·`ahead`·`duration`·`elapsed`)는 지금 시각을 부르는 쪽에서 받는다 — 화면은 그것을
// 시계(`hooks/useNow.ts`)에서 읽는다. 기본값으로 `Date.now()` 를 두면 렌더에서 그냥 불려, 같은 응답에 다시 그리지 않는
// 화면(`ui/share.ts`)에서 글자가 멈춘다. 화면의 상대 시각은 `components/Ago.tsx` 다.

/** 언제였나. `방금`·`3분 전`·`5시간 전`·`2일 전`. */
export function ago(iso: string | null | undefined, now: number): string {
  if (!iso) return DASH
  const ms = now - Date.parse(iso)
  if (!Number.isFinite(ms)) return DASH
  const m = Math.floor(ms / 60000)
  if (m < 1) return '방금'
  if (m < 60) return `${m}분 전`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 전`
  return `${Math.floor(h / 24)}일 전`
}

/** 앞으로 남은 때. `12분 뒤`, `5시간 뒤`, `3일 뒤` — 1분 안이거나 지났으면 `곧` 이다(기한을 넘긴 일은 다음 스윕이 한다). */
export function ahead(iso: string | null | undefined, now: number): string {
  if (!iso) return DASH
  const ms = Date.parse(iso) - now
  if (!Number.isFinite(ms)) return DASH
  const m = Math.floor(ms / 60000)
  if (m < 1) return '곧'
  if (m < 60) return `${m}분 뒤`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 뒤`
  return `${Math.floor(h / 24)}일 뒤`
}

/** 분까지의 시각. 목록 칸처럼 초가 결정에 쓰이지 않는 자리의 것이다. */
export function minute(iso: string | null | undefined): string {
  const s = exact(iso)
  return s === DASH ? s : s.slice(0, 16)
}

/** 정확한 시각. 전문가의 보조 줄에서 `ago` 옆에 놓인다. */
export function exact(iso: string | null | undefined): string {
  if (!iso) return DASH
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return DASH
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/*
 * 크기의 사다리. 크기의 단위는 여기서만 고른다 — 화면 코드는 `GB`·`TB` 를 직접 적지 않는다.
 * **기수는 1024 다**: API 의 `_mb`·`_gb` 가 2^20·2^30 바이트로 나눈 값이라, 디스크 한 장의 바이트 수도 같은 셈법으로
 * 옮겨야 한 화면의 디스크와 그 디스크로 만든 풀의 수를 견줄 수 있다. 그래서 제조사 라벨 4TB 디스크는 `3.6TB` 다.
 * 입력은 GiB 이고(MB 는 `gib` 로 옮기거나 `sizeMb`), **1024GiB 부터 TB** 다.
 * 수는 10 미만이 소수 한 자리이고 그 위는 정수다. GB 의 정수는 그대로 둔다 — 사양의 `8GB` 가 `8.0GB` 가 되지 않게.
 * **단위는 반올림한 수로 고른다** — 날 값으로 고르면 1023.6GiB 가 GB 에 남아 사다리에 없는 `1024GB` 가 된다.
 * **0 과 0 이 아닌 값은 글자에서도 갈린다.** 정확한 0 은 어느 단위에서나 `0` 이다(`0TB`·`0/15 TB`). 0 이 아닌 값이
 * 그 단위의 소수 한 자리에서 `0.0` 이 되면 `<0.1` 로 적는다 — 14TB 옆의 32GB 할당은 `<0.1TB` 다. `0.0TB` 는 있는 할당을
 * 없는 것처럼 보이게 하고, 작은 쪽만 GB 로 내리면 한 문자열의 단위가 갈리며, 소수를 늘리면 값마다 자리 수가 달라진다.
 */

export type SizeUnit = 'GB' | 'TB'

const SIZE_DIV: Record<SizeUnit, number> = { GB: 1, TB: 1024 }

/** MB 로 오는 크기를 GiB 로. 없는 값은 `null` — 0 이 아니다. */
export function gib(mb: number | null | undefined): number | null {
  return mb === null || mb === undefined ? null : mb / 1024
}

/** 그 크기가 적힐 단위. GB 로 반올림해 1024 가 되면 TB 다. 값이 없으면 GB. */
export function sizeUnit(gib: number | null | undefined): SizeUnit {
  return gib !== null && gib !== undefined && Number.isFinite(gib) && Math.round(gib) >= SIZE_DIV.TB ? 'TB' : 'GB'
}

/** 크기를 정해진 단위의 수로 — 단위는 붙이지 않는다. 없는 값은 `—`, 정확한 0 은 `0`, `0.0` 이 될 작은 값은 `<0.1`. */
export function sizeNumber(gib: number | null | undefined, unit: SizeUnit): string {
  if (gib === null || gib === undefined || !Number.isFinite(gib)) return DASH
  const v = Math.max(0, gib / SIZE_DIV[unit])
  if (v === 0) return '0'
  if (v >= 10) return String(Math.round(v))
  if (unit === 'GB' && Number.isInteger(v)) return String(v)
  const text = v.toFixed(1)
  if (text === '0.0') return '<0.1'
  return text === '10.0' ? '10' : text
}

/** 크기 하나 — 단위는 그 값이 정한다. `16GB`·`1.5GB`·`1023GB`·`1.0TB`. 없는 값은 `—`. */
export function size(gib: number | null | undefined): string {
  const unit = sizeUnit(gib)
  const n = sizeNumber(gib, unit)
  return n === DASH ? DASH : `${n}${unit}`
}

/** MB 로 오는 크기(메모리·VRAM) 하나. */
export function sizeMb(mb: number | null | undefined): string {
  return size(gib(mb))
}

/**
 * 두 크기가 한 문자열인 자리(쓴 양/총량·할당/총량)의 수와 단위. **총량이 단위를 정하고 둘 다 그 단위다** —
 * 총량을 모르면 쓴 양이 정한다. 단위는 인자가 아니라 돌려받는 값이라 `500GB / 4TB` 같은 갈림은 만들 수 없다.
 */
export function sizePair(used: number | null | undefined, total: number | null | undefined): { used: string; total: string; unit: SizeUnit } {
  const unit = sizeUnit(total ?? used)
  return { used: sizeNumber(used, unit), total: sizeNumber(total, unit), unit }
}

/** 문장 속의 두 크기 `쓴 양 / 총량단위` — `8 / 64GB`·`0.5 / 4.0TB`. 총량을 모르면 `8GB / —`, 둘 다 없으면 `—`. */
export function sizeUsage(used: number | null | undefined, total: number | null | undefined): string {
  const p = sizePair(used, total)
  if (p.used === DASH && p.total === DASH) return DASH
  return p.total === DASH ? `${p.used}${p.unit} / ${DASH}` : `${p.used} / ${p.total}${p.unit}`
}

/**
 * 바이트 수. GB 아래는 MB 이고, 그 위는 크기의 사다리(`sizeUnit`)를 소수 한 자리로 — `1.5GB`·`1.2TB`.
 * MB 에서 GB 로 넘는 자리도 반올림한 수로 고른다 — `1024MB` 가 나오지 않게.
 */
export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH
  const mb = n / 1048576
  if (Math.round(mb) < 1024) return `${Math.round(mb)}MB`
  const unit = sizeUnit(mb / 1024)
  return `${(mb / 1024 / SIZE_DIV[unit]).toFixed(1)}${unit}`
}

/** 시작 시각부터 지금까지의 점유 시간. `3시간 12분`, `2일 4시간`, 1분 미만은 `1분 미만`. */
export function duration(fromIso: string | null | undefined, now: number): string {
  if (!fromIso) return DASH
  const ms = now - Date.parse(fromIso)
  if (!Number.isFinite(ms) || ms < 0) return DASH
  const m = Math.floor(ms / 60000)
  if (m < 1) return '1분 미만'
  const h = Math.floor(m / 60)
  if (h < 1) return `${m}분`
  const d = Math.floor(h / 24)
  if (d < 1) return `${h}시간 ${m % 60}분`
  return `${d}일 ${h % 24}시간`
}

/**
 * 그 걸음에 머문 시간. `12초`, `1분 40초`, `3시간 12분`, `2일 4시간`.
 * `ago` 는 "언제였나"(`3분 전`)를 말하고 이것은 "얼마나 됐나"를 말한다 — 진행 줄은 뒤에 `째` 를 붙인다.
 * `duration` 과 달리 초를 센다: 걸음 하나는 몇십 초에 끝나기도 해서 `1분 미만` 으로는 흐르는지 멈췄는지 알 수 없다.
 */
export function elapsed(iso: string | null | undefined, now: number): string {
  if (!iso) return DASH
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return DASH
  // 노드의 시계가 앞서 있으면 음수가 온다. 아직 아무 시간도 지나지 않은 것이다.
  const s = Math.floor(Math.max(0, now - t) / 1000)
  if (s < 60) return `${s}초`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}분 ${s % 60}초`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 ${m % 60}분`
  return `${Math.floor(h / 24)}일 ${h % 24}시간`
}

/** 사용률. `used/total` 을 `42%` 로. 값이 없거나 총량이 0 이면 `—`. */
export function percent(used: number | null | undefined, total: number | null | undefined): string {
  if (used === null || used === undefined || total === null || total === undefined) return DASH
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return DASH
  const p = Math.min(100, Math.max(0, Math.round((used / total) * 100)))
  return `${p}%`
}

const RATE_UNITS = ['K', 'M', 'G', 'T'] as const

/** 초당 바이트를 `1.2M/s` 로. 단위는 K·M·G·T(1024 기수), K 가 최소 단위이고 10 미만은 소수 한 자리. 값이 없으면 `—`. */
export function rate(bytesPerSecond: number | null | undefined): string {
  if (bytesPerSecond === null || bytesPerSecond === undefined || !Number.isFinite(bytesPerSecond) || bytesPerSecond < 0) return DASH
  let v = bytesPerSecond / 1024
  let i = 0
  while (v >= 1024 && i < RATE_UNITS.length - 1) {
    v /= 1024
    i++
  }
  const text = v < 10 ? v.toFixed(1).replace(/\.0$/, '') : String(Math.round(v))
  return `${text}${RATE_UNITS[i]}/s`
}

/**
 * 쓴 양과 전체가 한 문자열인 자리의 꼴은 하나다 — `12 / 16코어`·`2.0 / 4GB`. 빗금 양옆을 띄우고 단위는 끝에 한 번 붙인다.
 * 남은 양을 따로 적지 않는다: 남음·가용·여유가 같은 뜻으로 갈렸고, 클라우드 콘솔도 사용량과 한도를 나란히 둘 뿐이다.
 * 정수는 소수점 없이 적고(`4코어`), 재서 얻은 사용량만 10 미만에서 소수 한 자리다(`0.6 / 2코어`).
 */

const countNumber = (n: number) => (Number.isInteger(n) || n >= 10 ? String(Math.round(n)) : n.toFixed(1))

/** 개수의 사용량/전체. `0.6 / 2코어` — 값이 없거나 전체가 0 이면 `—`. 크기는 `sizeFraction`. */
export function fraction(used: number | null | undefined, total: number | null | undefined, unit: string): string {
  if (used === null || used === undefined || total === null || total === undefined) return DASH
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return DASH
  return `${countNumber(Math.max(0, used))} / ${Math.round(total)}${unit}`
}

/** 크기(GiB)의 사용량/전체 — `2.0 / 4GB`·`0.5 / 4.0TB`. 전체가 단위를 정한다. 값이 없거나 전체가 0 이면 `—`. */
export function sizeFraction(used: number | null | undefined, total: number | null | undefined): string {
  if (used === null || used === undefined || total === null || total === undefined) return DASH
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return DASH
  return sizeUsage(used, total)
}

/** 개수 하나. `4코어`·`2장`·`3.2코어` — 장 수는 정수. 값이 없으면 `—`. */
export function amountText(n: number | null | undefined, unit: string): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH
  const v = Math.max(0, n)
  return `${unit === '장' ? String(Math.round(v)) : countNumber(v)}${unit}`
}

/**
 * 막대 하나의 글자 — `할당 / 전체` 의 수, 그 단위, `사용량 n단위`. 막대 줄은 수와 단위를 다른 칸에 그려서 둘을 따로 돌려준다.
 * 전체를 모르면 `4 / —` — 할당은 우리 원장이라 늘 있다. 전체 0 은 아는 값이라 `0 / 0`.
 */
export type PoolText = { amount: string; unit: string; used: string }

/** 개수의 막대(`코어`·`장`). 장 수는 정수. */
export function poolText(allocated: number, total: number | null, used: number | null, unit: string): PoolText {
  const a = amountText(allocated, unit).slice(0, -unit.length)
  const t = total === null || !Number.isFinite(total) ? DASH : String(Math.round(Math.max(0, total)))
  return { amount: `${a} / ${t}`, unit, used: `사용량 ${used === null ? DASH : amountText(used, unit)}` }
}

/** 크기의 막대(GiB). 단위는 전체가, 전체를 모르면 할당이 정하고 할당·전체·사용량이 모두 그 단위다. */
export function poolSizeText(allocated: number, total: number | null, used: number | null): PoolText {
  const p = sizePair(allocated, total)
  return { amount: `${p.used} / ${p.total}`, unit: p.unit, used: `사용량 ${used === null ? DASH : `${sizeNumber(used, p.unit)}${p.unit}`}` }
}
